// Role-aware navigation and route guards (SM-722).
//
// broker-service is the ONLY authority: its role × capability table
// (plan D61, broker-service internal/domain/staff.go) decides every
// request, and a route this module hides still answers the server's 403
// when deep-linked. What lives here is the portal's reading of the
// capabilities GET /api/broker/me hands back per membership, so the
// navigation offers only what the server would allow and a guard can say
// "this screen needs FINANCE_VIEW" before the call is made.
//
// Scope rule (D61 "scope" column, as changed by D183): VIEW, FINANCE_VIEW,
// CHILD_CREATE and — since the PO decision of 2026-10-08 (SM-852) —
// STAFF_MANAGE reach the membership's node AND its descendants: a parent's
// OWNER or ADMINISTRATOR staffs its sub-brokers under the same grant
// matrix (lib/staffAccess.ts). ACCOUNT_PAUSE, FUNDING_REQUEST and
// FEE_MANAGE apply on the membership's OWN node only — never inherited
// from an ancestor. BRANDING_MANAGE (SM-978: set or remove the node's GOLD
// currency image) reaches descendants like STAFF_MANAGE: a parent's OWNER
// or ADMINISTRATOR brands its sub-brokers, never the reverse.
//
// FEE_MANAGE (SM-835 / SM-842) — authoring the tree's fee plans and
// revenue-share plans — is listed only for an ACTIVE OWNER or FINANCE
// membership on a ROOT node that is not the SYSTEM_BROKER. A root owner
// who selects a sub-node has no FEE_MANAGE there, so the authoring screens
// anchor to the root membership node (feeManageRoot) instead of the
// selected one.
import { STAFF_CAPABILITIES } from "./types.ts";
import type { StaffCapability, StaffMeResponse, StaffPathResponse, StaffScopeItem } from "./types.ts";

/** The capabilities an ancestor's membership passes down. FEE_MANAGE never is. */
export const INHERITED_CAPABILITIES: ReadonlySet<StaffCapability> = new Set<StaffCapability>([
  "VIEW",
  "FINANCE_VIEW",
  "STAFF_MANAGE",
  "CHILD_CREATE",
  "BRANDING_MANAGE",
]);

// --- navigation -----------------------------------------------------------------

export type NavSection = "overview" | "organization" | "trading" | "money";

/** The sidebar's sections, in order. An item whose section is not here is never shown. */
export const NAV_SECTIONS: readonly NavSection[] = ["overview", "organization", "trading", "money"];

export type NavKey =
  | "overview"
  | "organization"
  | "hierarchy"
  | "staff"
  | "users"
  | "positions"
  | "orders"
  | "orderHistory"
  | "trades"
  | "risk"
  | "funding"
  | "fees"
  | "feePlans"
  | "revenue"
  | "payouts"
  | "statements"
  | "settlement"
  | "reconciliation";

export interface NavItem {
  key: NavKey;
  to: string;
  section: NavSection;
  /** The capability the screen's reads need on the selected node. */
  requires: StaffCapability;
  /** Lit on its own path only, not on the routes under it (/orders, not /orders/history). */
  exact?: boolean;
}

/** Every portal screen, in sidebar order, with what it reads. */
export const NAV_ITEMS: readonly NavItem[] = [
  { key: "overview", to: "/", section: "overview", requires: "VIEW" },
  { key: "organization", to: "/organization", section: "organization", requires: "VIEW" },
  { key: "hierarchy", to: "/hierarchy", section: "organization", requires: "VIEW" },
  { key: "staff", to: "/staff", section: "organization", requires: "VIEW" },
  { key: "users", to: "/users", section: "organization", requires: "VIEW" },
  { key: "risk", to: "/risk", section: "organization", requires: "VIEW" },
  // Trading (SM-829, read-only): live state first, history after.
  { key: "orders", to: "/orders", section: "trading", requires: "VIEW", exact: true },
  { key: "positions", to: "/positions", section: "trading", requires: "VIEW" },
  { key: "orderHistory", to: "/orders/history", section: "trading", requires: "VIEW" },
  { key: "trades", to: "/trades", section: "trading", requires: "VIEW" },
  // exact: /fees/plans is plan management's own screen, not the Fees page's
  { key: "fees", to: "/fees", section: "money", requires: "VIEW", exact: true },
  // Plan management (SM-844): the root's OWNER and FINANCE only.
  { key: "feePlans", to: "/fees/plans", section: "money", requires: "FEE_MANAGE" },
  { key: "funding", to: "/funding", section: "money", requires: "FINANCE_VIEW" },
  { key: "revenue", to: "/revenue", section: "money", requires: "FINANCE_VIEW" },
  { key: "payouts", to: "/payouts", section: "money", requires: "FINANCE_VIEW" },
  { key: "statements", to: "/statements", section: "money", requires: "FINANCE_VIEW" },
  { key: "settlement", to: "/settlement", section: "money", requires: "FINANCE_VIEW" },
  { key: "reconciliation", to: "/reconciliation", section: "money", requires: "FINANCE_VIEW" },
];

export function hasCapability(caps: readonly StaffCapability[] | null | undefined, cap: StaffCapability): boolean {
  return !!caps && caps.includes(cap);
}

/** The screens the selected node's capabilities open, in sidebar order. */
export function visibleNav(caps: readonly StaffCapability[] | null | undefined): NavItem[] {
  return NAV_ITEMS.filter((item) => hasCapability(caps, item.requires));
}

export function navItem(key: NavKey): NavItem {
  const item = NAV_ITEMS.find((n) => n.key === key);
  if (!item) throw new Error(`unknown nav key ${key}`);
  return item;
}

/**
 * Whether `path` is this item's screen: the overview and an `exact` item
 * match their own path only; any other item also every route under it.
 */
export function isNavActive(item: Pick<NavItem, "to" | "exact">, path: string): boolean {
  if (item.to === "/") return path === "/";
  if (item.exact) return path === item.to || path === `${item.to}/`;
  return path === item.to || path.startsWith(`${item.to}/`);
}

// --- route guard ------------------------------------------------------------------

export type GuardDecision =
  | { kind: "allow" }
  /** The role lacks the capability. The screen renders the SERVER's 403 by
   *  calling its endpoint anyway (never a blank page, never a silent
   *  redirect), so the refusal the user reads is the authority's own. */
  | { kind: "forbidden"; requires: StaffCapability };

export function guardRoute(caps: readonly StaffCapability[] | null | undefined, requires: StaffCapability): GuardDecision {
  return hasCapability(caps, requires) ? { kind: "allow" } : { kind: "forbidden", requires };
}

// --- memberships and the selected node ------------------------------------------

export function activeMemberships(me: StaffMeResponse | null | undefined): StaffScopeItem[] {
  return (me?.memberships ?? []).filter((m) => m.membership.status === "ACTIVE");
}

export function invitedMemberships(me: StaffMeResponse | null | undefined): StaffScopeItem[] {
  return (me?.memberships ?? []).filter((m) => m.membership.status === "INVITED");
}

export function suspendedMemberships(me: StaffMeResponse | null | undefined): StaffScopeItem[] {
  return (me?.memberships ?? []).filter((m) => m.membership.status === "SUSPENDED");
}

/**
 * The node a session lands on without a `?node=`: the ACTIVE membership
 * highest in its tree (smallest depth), ties by display name then id, so
 * the choice is stable across reloads.
 */
export function defaultNodeId(me: StaffMeResponse | null | undefined): string | undefined {
  const active = activeMemberships(me)
    .slice()
    .sort(
      (a, b) =>
        a.broker.broker_depth - b.broker.broker_depth ||
        a.broker.display_name.localeCompare(b.broker.display_name) ||
        a.broker.broker_id.localeCompare(b.broker.broker_id),
    );
  return active[0]?.broker.broker_id;
}

/** The ACTIVE membership sitting ON `nodeId`, if any. */
export function membershipOn(me: StaffMeResponse | null | undefined, nodeId: string): StaffScopeItem | undefined {
  return activeMemberships(me).find((m) => m.broker.broker_id === nodeId);
}

/**
 * What the caller may do on `nodeId`, from the ACTIVE memberships that
 * cover it. `pathIds` is GET /{node}/path's breadcrumb (covering
 * membership node → `nodeId`); it is only needed when `nodeId` is not a
 * membership node itself. A membership ON the node grants all of its
 * capabilities; a membership on an ANCESTOR grants only the inherited
 * ones. Stable order = contracts AllStaffCapabilities().
 */
export function effectiveCapabilities(
  me: StaffMeResponse | null | undefined,
  nodeId: string,
  pathIds: readonly string[] = [],
): StaffCapability[] {
  const granted = new Set<StaffCapability>();
  for (const m of activeMemberships(me)) {
    const on = m.broker.broker_id;
    if (on === nodeId) {
      for (const c of m.capabilities ?? []) granted.add(c);
    } else if (pathIds.includes(on)) {
      for (const c of m.capabilities ?? []) if (INHERITED_CAPABILITIES.has(c)) granted.add(c);
    }
  }
  return STAFF_CAPABILITIES.filter((c) => granted.has(c));
}

export type ScopeResolution =
  /** The node and what the caller may do there. */
  | { kind: "ok"; nodeId: string; capabilities: StaffCapability[]; onMembershipNode: boolean }
  /** A descendant: its breadcrumb must be read to know which membership covers it. */
  | { kind: "needs-path"; nodeId: string }
  /** No ACTIVE membership at all (only invitations, or only suspended ones). */
  | { kind: "no-active-membership"; invited: number; suspended: number };

/**
 * Resolve the selected node. `requested` is the URL's `?node=`; `path` is
 * its breadcrumb when one was read. A node the caller does not cover is
 * not decided here: its /path read answers the server's 404, and the
 * shell renders that.
 */
export function resolveScope(
  me: StaffMeResponse | null | undefined,
  requested: string | undefined,
  path?: StaffPathResponse | null,
): ScopeResolution {
  const nodeId = requested || defaultNodeId(me);
  if (!nodeId) {
    return {
      kind: "no-active-membership",
      invited: invitedMemberships(me).length,
      suspended: suspendedMemberships(me).length,
    };
  }
  if (membershipOn(me, nodeId)) {
    const pathIds = path && path.broker_id === nodeId ? path.path.map((p) => p.broker_id) : [];
    return { kind: "ok", nodeId, capabilities: effectiveCapabilities(me, nodeId, pathIds), onMembershipNode: true };
  }
  if (!path || path.broker_id !== nodeId) return { kind: "needs-path", nodeId };
  return {
    kind: "ok",
    nodeId,
    capabilities: effectiveCapabilities(
      me,
      nodeId,
      path.path.map((p) => p.broker_id),
    ),
    onMembershipNode: false,
  };
}

// --- fee and revenue-share authoring (FEE_MANAGE, SM-835) ------------------------

/**
 * The root of the tree `nodeId` belongs to, as the covering membership's
 * broker names it: GET /{node}/path starts at the covering membership node.
 * undefined when no ACTIVE membership covers the node.
 */
export function treeRootOf(
  me: StaffMeResponse | null | undefined,
  nodeId: string,
  path?: StaffPathResponse | null,
): string | undefined {
  const coveringId = path && path.broker_id === nodeId ? (path.path[0]?.broker_id ?? nodeId) : nodeId;
  return membershipOn(me, coveringId)?.broker.root_broker_id;
}

/**
 * The ACTIVE membership through which the caller authors `rootId`'s fee and
 * revenue-share plans: one ON that root node listing FEE_MANAGE (the server
 * lists it only for a root OWNER or FINANCE). The authoring screens anchor
 * to its node whatever node is selected, because FEE_MANAGE is never
 * inherited by a descendant.
 */
export function feeManageRoot(me: StaffMeResponse | null | undefined, rootId: string | undefined): StaffScopeItem | undefined {
  if (!rootId) return undefined;
  return activeMemberships(me).find(
    (m) => m.is_root && m.broker.broker_id === rootId && (m.capabilities ?? []).includes("FEE_MANAGE"),
  );
}
